import {AfterViewInit, Component, OnDestroy, OnInit} from '@angular/core';
import {CompanyService, ProjectService, ScreenService} from '@app/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {GlobalSearchService} from '../../../../core/services/global-search.service';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  fromEvent,
  Subject,
  switchMap,
  takeUntil,
  tap
} from 'rxjs';
import {FormControl} from '@angular/forms';
import {animate, style, transition, trigger} from '@angular/animations';
import {NavigationEnd, Router} from '@angular/router';

@Component({
  selector: 'app-global-searchbar',
  standalone: false,

  templateUrl: './global-searchbar.component.html',
  styleUrl: './global-searchbar.component.less',
  animations: [
    trigger('slideDownUp', [
      transition(':enter', [
        style({height: 0, opacity: 0}),
        animate('200ms ease-out', style({height: '*', opacity: 1}))
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({height: 0, opacity: 0}))
      ])
    ])
  ]
})
export class GlobalSearchbarComponent implements OnInit, OnDestroy, AfterViewInit {
  searchControl = new FormControl<string>('');
  destroy$ = new Subject<void>();
  companyId: number = 0;

  loading: boolean = false;
  isFiltering: boolean = false;
  isSearchPage: boolean = false;


  constructor(
    private globalSearchService: GlobalSearchService,
    private projectService: ProjectService,
    private companyService: CompanyService,
    public screenService: ScreenService,
    private router: Router
  ) {
    companyService.selectedCompanyId$.pipe(takeUntilDestroyed()).subscribe(companyId => {
      this.companyId = companyId || 0;
    });

    router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntilDestroyed()
    ).subscribe(event => {
      const url = event as NavigationEnd;
      if (url.url.includes('/search')) {
        this.isSearchPage = true;
      }
    });
  }

  ngOnInit(): void {
    // Escucha los cambios en el input de busqueda y
    // los transforma en una peticion de busqueda solo si
    // hay 3 o mas caracteres
    this.searchControl.valueChanges
      .pipe(
        // Espera 500ms antes de realizar una busqueda
        debounceTime(500),

        // Filtra valores nulos
        filter((text): text is string => text != null),

        // Solo emite valores si cambian
        distinctUntilChanged(),

        // Cambia el estado de loading y isFiltering
        tap((text: string) => {
          this.isFiltering = text.length >= 3;
          this.loading = this.isFiltering;
          this.globalSearchService.loading$.next(this.loading)
        }),

        // Solo busca si hay 3 o más caracteres
        filter((text: string) => text.length >= 3),

        // Lanza la peticion de busqueda
        switchMap(search =>
          this.globalSearchService.search(search, this.companyId).pipe(
            // Finaliza el loading cuando termina la peticion
            finalize(() => {
              this.loading = false;
              this.globalSearchService.loading$.next(this.loading);
            })
          )
        ),

        // Cancela la suscripcion cuando se destruye el componente
        takeUntil(this.destroy$)
      )
      .subscribe(rsp => {
        this.globalSearchService.globalSearchValue = this.searchControl.value || '';
        this.globalSearchService.globalSearchResult = rsp;
      });
  }

  ngAfterViewInit(): void {
    fromEvent<MouseEvent>(document, 'click')
      .pipe(
        filter(event => {
          const target = event.target as HTMLElement;
          return target && !target.closest('.search-area-wrapper');
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.isFiltering = false;
      });
  }

  cleanSearch(): void {
    this.searchControl.setValue('');
    this.loading = false;
    this.isFiltering = false;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
